import React from "react";
import { Box } from "@twilio-paste/core";
import { useTheme } from "@twilio-paste/theme";
import { Menu, MenuButton, useMenuState } from "@twilio-paste/menu";
import AddReaction from "../icons/AddReaction";
import { Tooltip } from "@twilio-paste/core/tooltip";
import { Reactions, ReactionsType } from "../../types";
import ReactionItem from "./ReactionItem";
import { reactionsMapping } from "../../utils/reactionsMapping";

type ReactionsProps = {
  reactions?: ReactionsType;
  onReactionsChanged: (reactions: ReactionsType) => void;
  showAddReactionButton?: boolean;
  showAsLabel?: boolean;
  showReactionsCount?: boolean;
  callback?: () => void;
};

const ReactionsBox: React.FC<ReactionsProps> = ({
  reactions = {},
  onReactionsChanged,
  showAddReactionButton = true,
  showAsLabel = false,
  showReactionsCount = true,
  callback,
}: ReactionsProps) => {
  const menu = useMenuState({ placement: "top-start" });
  const user = localStorage.getItem("username") ?? "";
  const theme = useTheme();

  const onUpdateReaction = (reaction: Reactions) => {
    menu.hide();
    callback && callback();
    const reactionUsers = reactions?.[reaction] ?? [];

    onReactionsChanged({
      ...reactions,
      [reaction]: reactions?.[reaction]?.includes(user)
        ? reactionUsers.filter((participant) => participant !== user)
        : [...reactionUsers, user],
    });
  };

  const getReactionEmoji = (
    reactionId: Reactions,
    count: number
  ): React.ReactNode => {
    const emoji = reactionsMapping[reactionId];
    const reactionUsers = reactions?.[reactionId] || [];
    const userIncluded = reactionUsers.includes(user);

    if (emoji) {
      return (
        <Tooltip
          key={reactionId}
          text={
            userIncluded
              ? `You${reactionUsers.length > 1 ? " and" : ""} ${reactionUsers
                  .filter((name) => name !== user)
                  .join(", ")} reacted with :${reactionId}`
              : `${reactionUsers.join(", ")} reacted with :${reactionId}`
          }
        >
          <Box
            key={reactionId}
            style={{
              border: `1px solid ${
                userIncluded
                  ? theme.textColors.colorTextLink
                  : theme.textColors.colorTextIconInverse
              }`,
              borderRadius: 10,
              margin: "4px 6px 0 0",
              backgroundColor: userIncluded
                ? "rgba(2, 99, 224, 0.08)"
                : theme.backgroundColors.colorBackgroundBody,
            }}
          >
            <ReactionItem
              emoji={emoji}
              reactionId={reactionId}
              count={count}
              key={reactionId}
              onAction={onUpdateReaction}
              user={user}
            />
          </Box>
        </Tooltip>
      );
    }
    return null;
  };

  const renderReactionBox = (reactionId: Reactions, count?: number) =>
    count ? getReactionEmoji(reactionId, count) : null;

  const reactionButtonStyle = showAsLabel
    ? {
        textDecoration: "none",
      }
    : {
        padding: "6px",
        border: `1px solid ${theme.borderColors.colorBorderWeak}`,
        borderRadius: 12,
        width: 34,
        height: 34,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "4px",
        background: theme.backgroundColors.colorBackgroundBody,
      };

  const renderReactionButton = () => (
    <Box style={{ ...reactionButtonStyle }}>
      <MenuButton
        {...menu}
        variant="link"
        size="reset"
        style={{
          width: showAsLabel ? 180 : 34,
          height: 34,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 8,
          padding: showAsLabel ? "0 10px" : 0,
          borderRadius: 12,
          textDecoration: "none",
        }}
      >
        {showAsLabel ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              gap: 8,
              padding: "5px",
            }}
          >
            <AddReaction />
            <span
              style={{
                fontWeight: 600,
                color: theme.textColors.colorTextLink,
              }}
            >
              Add reaction
            </span>
          </div>
        ) : (
          <AddReaction />
        )}
      </MenuButton>

      <Menu
        {...menu}
        aria-label="MessageReactions"
        style={{
          padding: 0,
          zIndex: 99,
          borderRadius: 16,
          overflow: "hidden",
          minWidth: 260,
          boxShadow: "0 14px 40px rgba(0,0,0,.18)",
          border: `1px solid ${theme.borderColors.colorBorderWeak}`,
        }}
      >
        {/* CARD */}
        <div style={{ background: theme.backgroundColors.colorBackgroundBody }}>
          {/* Header */}
          <div
            style={{
              padding: "12px 14px",
              borderBottom: `1px solid ${theme.borderColors.colorBorderWeak}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            Add reaction
          </div>

          {/* Grid emoji */}
          <div
            style={{
              padding: 12,
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 10,
              justifyItems: "center",
            }}
          >
            {Object.values(Reactions).map((reactionId) => {
              const emoji = reactionsMapping[reactionId];
              const reactionUsers = reactions?.[reactionId] ?? [];
              const isActive = reactionUsers.includes(user);

              return (
                <button
                  key={reactionId}
                  type="button"
                  onClick={() => onUpdateReaction(reactionId)}
                  aria-label={`React ${reactionId}`}
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 14,
                    border: `1px solid ${
                      isActive
                        ? theme.borderColors.colorBorderPrimary
                        : theme.borderColors.colorBorderWeak
                    }`,
                    background: isActive
                      ? "rgba(2, 99, 224, 0.10)"
                      : theme.backgroundColors.colorBackgroundBody,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: 18,
                    transition: "transform .08s ease, background .12s ease",
                  }}
                  onMouseDown={(e) => e.preventDefault()} // evita focus brutto “a scatto”
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(2, 99, 224, 0.06)";
                    e.currentTarget.style.transform = "scale(1.06)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.background = isActive
                      ? "rgba(2, 99, 224, 0.10)"
                      : theme.backgroundColors.colorBackgroundBody;
                  }}
                >
                  {emoji}
                </button>
              );
            })}
          </div>
        </div>
      </Menu>
    </Box>
  );

  return (
    <Box style={{ display: "flex", alignItems: "center" }}>
      {showReactionsCount &&
        Object.entries(reactions).map(([reactionId, users]) =>
          renderReactionBox(reactionId as Reactions, users.length)
        )}

      {showAddReactionButton && (
        <>
          {!showAsLabel ? (
            <Tooltip text="Add reaction">{renderReactionButton()}</Tooltip>
          ) : (
            <>{renderReactionButton()}</>
          )}
        </>
      )}
    </Box>
  );
};

export default ReactionsBox;
